import { ReactNode } from "react";

export interface TabProps {
  children: ReactNode;
  className?: string;
  isActive?: boolean;
  activeClassName?: string;
  inActiveClassName?: string;
  handleClick?: () => void | Promise<void>;
  index?: number;
  isComingSoon?: boolean;
  isDisabled?: boolean;
  icon?: JSX.Element;
  size?: "default" | "lg";
}

export function sizeToClass(s?: TabProps["size"]) {
  switch (s) {
    case "lg":
      return "text-base h-[56px] md:text-xl md:h-[84px]";
    default:
      return "md:text-sm h-[120px] md:h-[52px]";
  }
}

const styles = {
  base: "flex flex-col md:flex-row items-center border border-t-4 justify-center gap-y-2 gap-x-2 pt-0.5 px-2 md:px-6 rounded-t",
  active: "border-primary border-b-white bg-white text-primary font-bold",
  inactive:
    "bg-primary-thin border-x-primary-thin border-b-primary border-t-primary-thin",
  comingSoon:
    "overlay-comingsoon-text after:tracking-wider after:rounded-t after:pointer-events-none",
};

export function Tab({
  children,
  handleClick,
  className,
  activeClassName = "",
  inActiveClassName = "",
  icon,
  size = "default",
  isActive,
  isComingSoon,
  isDisabled,
}: TabProps) {
  if (isComingSoon) {
    return (
      <div className={[styles.comingSoon, className].join(" ")}>
        <button
          type="button"
          className={[
            styles.base,
            isActive
              ? `${styles.active} ${activeClassName}`
              : `${styles.inactive} ${inActiveClassName}`,
            "w-full",
            sizeToClass(size),
          ].join(" ")}
          aria-hidden={true}
          onClick={handleClick}
          disabled={isDisabled}
          role="tab"
        >
          {icon}
          {children}
        </button>
      </div>
    );
  } else {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={[
          styles.base,
          isActive
            ? `${styles.active} ${activeClassName}`
            : `${styles.inactive} ${inActiveClassName}`,
          className,
          sizeToClass(size),
        ].join(" ")}
        disabled={isDisabled}
        aria-selected={isActive}
        role="tab"
      >
        {icon}
        {children}
      </button>
    );
  }
}

interface Props {
  children: ReactNode;
  className?: string;
  classNameBorder?: string;
}

export default function PageTab({
  children,
  className,
  classNameBorder = "",
}: Props) {
  return (
    <nav className={className}>
      <div className="flex space-x-[5px] md:space-x-0.5" role="tablist">
        {children}
      </div>
      <div
        className={`"mt-[-1px] border-b border-primary ${classNameBorder}`}
      ></div>
    </nav>
  );
}
